import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/auth";
import type { useLocalRoster } from "./hooks";
import PlayerSettings from "./PlayerSettings";
import st from "./start.module.scss";
import s from "./PlayerMenu.module.scss";

// The navbar's player menu (design 2, "Locker", picked 2026-10-23): your player
// (PlayerSettings: name, keys, colour and what you wear, the same block as on
// the profile page), and the account: Sign in, or Profile and Sign out.

const svg = (d: React.ReactNode) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>;
const ICON = {
  profile: svg(<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>),
  out: svg(<path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />),
};

export default function PlayerMenu({ roster, onClose, onLogin, onAccount }: {
  roster: ReturnType<typeof useLocalRoster>;
  onClose: () => void;
  onLogin: () => void;
  onAccount: () => void;
}) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const toStore = (tab?: string) => { onClose(); navigate(tab ? "/shop?tab=" + tab : "/shop"); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const login = () => { onClose(); onLogin(); };
  const profile = () => { onClose(); onAccount(); };
  const out = () => { onClose(); void signOut(); };

  const accountButtons = user ? (
    <div className={s.footRow}>
      <button type="button" className={s.ghost} onClick={profile}>{ICON.profile}Profile</button>
      <button type="button" className={`${s.ghost} ${s.danger}`} onClick={out}>{ICON.out}Sign out</button>
    </div>
  ) : (
    <button type="button" className={s.cta} onClick={login}>Sign in</button>
  );

  return (
    <>
      {createPortal(<div className={st.popScrim} onClick={onClose} />, document.body)}
      <div className={`${st.popRoot} ${s.root}`} role="dialog" aria-label="Your player">
        <div className={s.menu}>
          <PlayerSettings roster={roster} onShop={toStore} />
          {accountButtons}
        </div>
      </div>
    </>
  );
}
